// All rankings, averages and proceeds are computed from siteData at runtime.

export const GROUP_OF_STATUS = {
  Sold: "sold",
  Active: "forSale",
  "First Look": "forSale",
  Pending: "pending",
};

export const STATUS_KEY = {
  Sold: "sold",
  Active: "active",
  "First Look": "firstLook",
  Pending: "pending",
};

// CSS variable for each status (tokens decide the actual color per theme).
export const STATUS_COLOR = {
  Sold: "var(--status-sold)",
  Active: "var(--status-active)",
  "First Look": "var(--status-pending)",
  Pending: "var(--status-pending)",
};

export function enrichComps(comps) {
  return comps.map((c, i) => ({
    ...c,
    index: i,
    group: GROUP_OF_STATUS[c.status] || "forSale",
    ppsf: c.price / c.sqft,
    pctOfOriginal: (c.price / c.originalPrice) * 100,
  }));
}

const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

export function summarize(list) {
  const doms = list.map((c) => c.daysOnMarket).filter((d) => d !== null && d !== undefined);
  return {
    count: list.length,
    avgPrice: Math.round(mean(list.map((c) => c.price))),
    avgPricePerSqft: Math.round(mean(list.map((c) => c.ppsf))),
    avgPctOfOriginal: Math.round(mean(list.map((c) => c.pctOfOriginal)) * 100) / 100,
    avgDaysOnMarket: doms.length ? Math.round(mean(doms)) : null,
  };
}

export function summarizeGroups(enriched) {
  const out = {};
  ["sold", "forSale", "pending"].forEach((g) => {
    const list = enriched.filter((c) => c.group === g);
    out[g] = list.length ? summarize(list) : null;
  });
  return out;
}

// Confirms runtime numbers match the brief's compsSummaryCheck.
export function checkSummaries(computed, expected) {
  const problems = [];
  Object.entries(expected || {}).forEach(([group, exp]) => {
    const got = computed[group];
    Object.entries(exp).forEach(([k, v]) => {
      const gv = k === "count" ? got?.count : got?.[k];
      if (gv !== v) problems.push(`${group}.${k}: expected ${v}, got ${gv}`);
    });
  });
  return { pass: problems.length === 0, problems };
}

export function rankings(subject, comps) {
  const total = comps.length;
  return {
    total,
    lotLarger: comps.filter((c) => c.lotSqft < subject.lotSqft).length,
    newer: comps.filter((c) => c.yearBuilt < subject.yearBuilt).length,
    moreBaths: comps.filter((c) => c.baths < subject.baths).length,
    moreGarage: comps.filter((c) => c.garage < subject.garage).length,
  };
}

// ---------- Proceeds ----------

export function titlePolicy(price, points) {
  const pts = [...points].sort((a, b) => a.price - b.price);
  if (price <= pts[0].price) return pts[0].fee;
  if (price >= pts[pts.length - 1].price) return pts[pts.length - 1].fee;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    if (price >= a.price && price <= b.price) {
      return a.fee + ((b.fee - a.fee) * (price - a.price)) / (b.price - a.price);
    }
  }
  return pts[pts.length - 1].fee;
}

// State REET is graduated by bracket; local REET is a flat rate on the full price.
export function exciseTax(price, excise) {
  const brackets = excise[`stateBrackets${excise.useYear}`];
  let state = 0;
  let lower = 0;
  for (const b of brackets) {
    const upper = b.upTo === null ? Infinity : b.upTo;
    if (price > lower) state += (Math.min(price, upper) - lower) * b.rate;
    if (price <= upper) break;
    lower = upper;
  }
  const local = price * excise.localRate;
  return { state: Math.round(state), local: Math.round(local) };
}

export function computeProceeds(price, cfg, listingPct, buyerPct) {
  const lp = Number.isFinite(listingPct) ? listingPct : 0;
  const bp = Number.isFinite(buyerPct) ? buyerPct : 0;
  const listing = Math.round((price * lp) / 100);
  const buyer = Math.round((price * bp) / 100);
  const title = Math.round(titlePolicy(price, cfg.titlePolicyPoints));
  const ex = exciseTax(price, cfg.excise);
  const prepTotal = cfg.prepItems.reduce((s, p) => s + (p.amount || 0), 0);
  const costs = listing + buyer + cfg.escrowFee + title + cfg.recordingFee + ex.state + ex.local + prepTotal;
  return {
    listing,
    buyer,
    escrow: cfg.escrowFee,
    title,
    recording: cfg.recordingFee,
    exciseState: ex.state,
    exciseLocal: ex.local,
    excise: ex.state + ex.local,
    prepTotal,
    costs,
    net: price - costs,
  };
}

export function checkProceeds(cfg) {
  const problems = [];
  Object.entries(cfg.mathCheck || {}).forEach(([p, expected]) => {
    const got = computeProceeds(Number(p), cfg, 2.5, 2.5).net;
    if (got !== expected) problems.push(`${p}: expected ${expected}, got ${got}`);
  });
  return { pass: problems.length === 0, problems };
}
